import React from 'react';
import { Route, RouteProps, Switch } from 'react-router-dom';
import { MaintenanceConfig, Splits } from './types/split.type';
import {
    Adjustments,
    Auth,
    CheckoutInstructions,
    GettingThere,
    InTheHome,
    Maintenance,
    ManageGuestConfirmation,
    ModifyGuests,
    ModifyTrip,
    PaymentConfirmationAdjustment,
    Receipt,
    RentalAgreement,
    VehicleRegistration,
    PaymentConfirmation,
    Reservation,
} from './views';
import { requiredAuth } from './auth';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import { TripSourceMultiplexer } from './components';
import { Payment } from './views/payment/Payment';
import { ExtendedStay } from './views/extended-stay/ExtendedStay';
import { ModifyPets } from './views/modify/ModifyPets';
import { Cancellation } from './views/adjustments/Cancellation';
import { useFeatureConfig } from './services/optimizely/optimizelyHelper';
import { PalReservation } from './views/pal/reservation/PalReservation';
import { PalRentalAgreement } from './views/pal/reservation/PalRentalAgreement';
import { RequestCancellation } from './views/pal/reservation/RequestCancellation';
import { PalReceipt } from './views/pal/reservation/PalReceipt';

const NotFound: React.FC = () => <h1>404 NOT FOUND</h1>;
const PALOrLegacyReservation: React.FC = () => (
    <TripSourceMultiplexer onLegacyVacasaTripRender={() => <Reservation />} onPALTripRender={() => <PalReservation />} />
);
const PALOrLegacyRentalAgreement: React.FC = () => (
    <TripSourceMultiplexer onLegacyVacasaTripRender={() => <RentalAgreement />} onPALTripRender={() => <PalRentalAgreement />} />
);
const PALOrLegacyCancellation: React.FC = () => (
    <TripSourceMultiplexer onLegacyVacasaTripRender={() => <Cancellation />} onPALTripRender={() => <RequestCancellation />} />
);
const PALOrLegacyReceipt: React.FC = () => (
    <TripSourceMultiplexer onLegacyVacasaTripRender={() => <Receipt />} onPALTripRender={() => <PalReceipt />} />
);

export enum AppRoutes {
    AUTH = '/trip/auth',
    RESERVATION = '/trip/reservation',
    GETTING_THERE = '/trip/getting-there',
    IN_THE_HOME = '/trip/in-the-home',
    RENTAL_AGREEMENT = '/trip/rental-agreement',
    VEHICLE_REGISTRATION = '/trip/vehicle-registration',
    CHECKOUT_INSTRUCTIONS = '/trip/checkout-instructions',
    MODIFY_TRIP = '/trip/modify-trip',
    MODIFY_TRIP_ADJUSTMENTS = '/trip/modify-trip/:adjustment',
    MODIFY_GUESTS = '/trip/modify-guests',
    MODIFY_PETS = '/trip/modify-pets',
    MODIFY_CONFIRMATION = '/trip/modify-confirmation',
    CANCEL = '/trip/cancel',
    PAYMENT = '/trip/payment',
    PAYMENT_THANK_YOU = '/trip/payment/thank-you',
    PAYMENT_SUCCESSFUL = '/trip/payment/success',
    RECEIPT = '/trip/receipt',
    EXTENDED_STAY = '/trip/extend_stay',
}

const routes: RouteProps[] = [
    { path: AppRoutes.AUTH, exact: true, component: Auth },
    { path: AppRoutes.RESERVATION, exact: true, component: requiredAuth(PALOrLegacyReservation) },
    { path: AppRoutes.MODIFY_TRIP_ADJUSTMENTS, exact: true, component: requiredAuth(Adjustments) },
    { path: AppRoutes.CANCEL, exact: true, component: requiredAuth(PALOrLegacyCancellation) },
    { path: AppRoutes.GETTING_THERE, exact: true, component: requiredAuth(GettingThere) },
    { path: AppRoutes.IN_THE_HOME, exact: true, component: requiredAuth(InTheHome) },
    { path: AppRoutes.RENTAL_AGREEMENT, exact: true, component: requiredAuth(PALOrLegacyRentalAgreement) },
    { path: AppRoutes.VEHICLE_REGISTRATION, exact: true, component: requiredAuth(VehicleRegistration) },
    { path: AppRoutes.MODIFY_TRIP, exact: true, component: requiredAuth(ModifyTrip) },
    { path: AppRoutes.CHECKOUT_INSTRUCTIONS, exact: true, component: requiredAuth(CheckoutInstructions) },
    { path: AppRoutes.MODIFY_GUESTS, exact: true, component: requiredAuth(ModifyGuests) },
    { path: AppRoutes.MODIFY_PETS, exact: true, component: requiredAuth(ModifyPets) },
    { path: AppRoutes.PAYMENT, exact: true, component: requiredAuth(Payment) },
    { path: AppRoutes.PAYMENT_SUCCESSFUL, exact: true, component: requiredAuth(PaymentConfirmationAdjustment) },
    { path: AppRoutes.PAYMENT_THANK_YOU, exact: true, component: requiredAuth(PaymentConfirmation) },
    { path: AppRoutes.MODIFY_CONFIRMATION, exact: true, component: requiredAuth(ManageGuestConfirmation) },
    { path: AppRoutes.RECEIPT, exact: true, component: requiredAuth(PALOrLegacyReceipt) },
    { path: AppRoutes.EXTENDED_STAY, exact: true, component: requiredAuth(ExtendedStay) },
    { path: '', exact: true, component: requiredAuth(PALOrLegacyReservation) },
    { path: '*', component: NotFound },
];

export const Routes = () => {
    const { isEnabled: isMaintenanceEnabled, config: maintenanceConfig } = useFeatureConfig<MaintenanceConfig>(
        Splits.GUESTS_PORTAL_MAINTENANCE,
        true
    );

    const pagesMaintenance = maintenanceConfig?.pages;

    return (
        <React.Fragment>
            <ScrollToTop />
            <Switch>
                {routes.map((route, i) => {
                    const path = String(route.path);
                    const pageMaintenance = pagesMaintenance && pagesMaintenance.indexOf(path);
                    if (isMaintenanceEnabled && pageMaintenance && pageMaintenance >= 0) route.component = Maintenance;
                    return <Route key={i} {...route} />;
                })}
            </Switch>
        </React.Fragment>
    );
};
